import { useEffect, useRef } from 'react'

/**
 * Intercepts the browser back button / swipe-back gesture while a dialog is
 * open and calls `onClose` instead of navigating away.
 *
 * Strategy:
 *  - Push a synthetic history entry when the dialog opens.
 *  - Listen for `popstate` (fired by back gesture/button) → close dialog.
 *  - If the dialog is dismissed by other means (X button, confirm, etc.),
 *    pop the synthetic entry so history stays clean.
 *
 * React Strict Mode note: In dev, effects run twice (mount → cleanup → remount).
 * The cleanup schedules history.back() as a microtask and cancels it on remount,
 * preventing the phantom popstate from closing the dialog immediately.
 *
 * Nested dialog note: When a nested dialog (e.g. SpotlightOverlay inside a
 * TaskDrawer) is dismissed programmatically, its cleanup calls history.back()
 * to pop its own synthetic entry. This would trigger the parent dialog's
 * popstate handler. We use a module-level counter to mark these programmatic
 * back calls so parent handlers can ignore them.
 */

// Counts in-flight programmatic history.back() calls from cleanup.
// Any popstate handler that sees this > 0 should skip closing its dialog.
let programmaticBacks = 0

// Counts how many dialog handlers are currently listening (i.e. open dialogs).
// Used to avoid incrementing programmaticBacks when no parent could be triggered
// (which would otherwise leak into the next test in jsdom / no-popstate envs).
let activeHandlers = 0

export function useDialogBackHandler(isOpen: boolean, onClose: () => void): void {
  // Keep a stable ref so the effect doesn't need to re-run when onClose changes
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose })

  // Used to cancel a pending cleanup back() if the effect re-mounts (Strict Mode)
  const pendingBackRef = useRef(false)

  useEffect(() => {
    if (!isOpen) return

    // Cancel any back() that was scheduled by a previous cleanup (Strict Mode)
    pendingBackRef.current = false
    activeHandlers++

    let closedByBack = false
    history.pushState({ dialogBack: true }, '')

    const handler = () => {
      // Ignore popstate events triggered by our own programmatic history.back()
      // calls (nested dialog cleanup) — they are not real back-button presses.
      if (programmaticBacks > 0) {
        programmaticBacks--
        return
      }
      closedByBack = true
      onCloseRef.current()
    }

    window.addEventListener('popstate', handler)

    return () => {
      activeHandlers--
      window.removeEventListener('popstate', handler)
      // Dialog was closed by something other than the back gesture → pop the
      // synthetic entry we pushed so the browser history stays in sync.
      // BUT: if a navigation already happened (e.g. clicking a link inside the
      // dialog), the history state is no longer ours — skip back() in that case
      // to avoid undoing the navigation.
      if (!closedByBack && history.state?.dialogBack === true) {
        // Schedule as microtask: if this is a Strict Mode double-invoke, the
        // re-mount will set pendingBackRef.current = false before this fires.
        pendingBackRef.current = true
        void Promise.resolve().then(() => {
          if (pendingBackRef.current) {
            pendingBackRef.current = false
            // Only signal a programmatic back when a parent handler is still
            // active (activeHandlers > 0).  If there is no parent, nobody will
            // decrement the counter and it would leak between test cases.
            if (activeHandlers > 0) {
              programmaticBacks++
            }
            history.back()
          }
        })
      }
    }
  }, [isOpen])
}
